import React, { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { X, Lock, Users, ShieldCheck } from "lucide-react";
import { toast } from "@/components/Toaster";

/**
 * Admin-only dialog to manage per-document/-folder access.
 *  - `Restricted` toggle: when on, only Admins + Allowed members can see it
 *    (cascades to children when applied to a folder).
 *  - `Allowed members` checklist: pick who can see this restricted item.
 *
 * Calls PATCH /api/rooms/{roomId}/documents/{docId}/access
 *   body: { restricted: bool, allowed_users: [user_id, ...] }
 */
export default function ManageAccessDialog({ roomId, doc, onClose, onSaved }) {
  const [restricted, setRestricted] = useState(false);
  const [allowed, setAllowed] = useState(new Set());
  const [members, setMembers] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const m = await api.get(`/rooms/${roomId}/members`);
        if (cancelled) return;
        setMembers(m.data || []);
        setRestricted(!!doc.restricted);
        setAllowed(new Set(doc.allowed_users || []));
      } catch (e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [roomId, doc]);

  const toggleUser = (uid) => {
    setAllowed((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.patch(
        `/rooms/${roomId}/documents/${doc.id}/access`,
        { restricted, allowed_users: Array.from(allowed) },
      );
      toast.success(restricted ? "Access restricted" : "Open to all members");
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-6" onClick={onClose}>
      <form
        onSubmit={save}
        onClick={(e) => e.stopPropagation()}
        className="bg-card border border-border rounded-md w-full max-w-md p-6 relative"
        data-testid="manage-access-dialog"
      >
        <button
          type="button" onClick={onClose} aria-label="Close"
          className="absolute top-3 right-3 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-start gap-3 pr-6">
          <div className="w-10 h-10 rounded-md bg-primary/15 flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-5 h-5 text-primary" />
          </div>
          <div className="min-w-0">
            <h3 className="font-display text-lg tracking-tight">Manage access</h3>
            <p className="mt-1 text-sm text-muted-foreground truncate">
              {doc.type === "folder" ? "Folder" : "File"}: <span className="font-mono">{doc.name}</span>
            </p>
          </div>
        </div>

        <label className="mt-5 flex items-start gap-3 cursor-pointer border border-border rounded-md p-3 hover:bg-secondary transition">
          <input
            data-testid="manage-access-restricted"
            type="checkbox" checked={restricted}
            onChange={(e) => setRestricted(e.target.checked)}
            className="mt-1 accent-primary"
          />
          <div className="text-sm">
            <div className="font-medium flex items-center gap-2">
              <Lock className="w-3.5 h-3.5" /> Restrict access
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              When on, only Admins and the members you select below can see this
              {doc.type === "folder" ? " folder and everything inside it" : " file"}.
            </div>
          </div>
        </label>

        {restricted && (
          <div className="mt-4">
            <div className="label-mono text-muted-foreground mb-2 flex items-center gap-2">
              <Users className="w-3.5 h-3.5" /> Allowed members
            </div>
            <div className="border border-border rounded-md max-h-56 overflow-y-auto divide-y divide-border">
              {members.length === 0 ? (
                <div className="px-4 py-6 text-sm text-muted-foreground text-center">
                  No members yet. Invite people first, then come back to grant access.
                </div>
              ) : members.map((m) => (
                <label key={m.user_id} className="flex items-center gap-3 px-3 py-2 hover:bg-secondary transition cursor-pointer">
                  <input
                    data-testid={`manage-access-user-${m.user_id}`}
                    type="checkbox" checked={allowed.has(m.user_id)}
                    onChange={() => toggleUser(m.user_id)}
                    className="accent-primary"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate">{m.name || m.email}</div>
                    <div className="mono text-[10px] text-muted-foreground truncate">{m.email}</div>
                  </div>
                  <span className="mono text-[10px] uppercase tracking-wider text-muted-foreground">{m.role}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6 flex gap-2 justify-end">
          <button
            type="button" onClick={onClose}
            className="px-4 py-2 text-sm rounded-md border border-border hover:bg-secondary transition"
          >
            Cancel
          </button>
          <button
            data-testid="manage-access-save"
            type="submit" disabled={busy}
            className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition disabled:opacity-50"
          >
            {busy ? "Saving…" : "Save access"}
          </button>
        </div>
      </form>
    </div>
  );
}
